import { dispatch, state } from '__common/store';
import { getRoofEdges } from '__editor/panelsEditor/components/background/background';
import { SET_STAGE_SCALE, SET_CENTER_OFFSET, LOAD_PANELS } from 'actions';
import { resetKdTree } from '../cursor/utils/kdTreeStore';
import { clearRTree } from '../panels/panelsCollisions';
import { removeAllPanelsFromStageToRedraw } from '../panels/drawingPanelsOnStage';
import { CENTER_OBSTRUCTIONS_ON_ROOF } from '../obstructions/ObstructionsActions';
import { getBaysContainer, getPanelsContainer } from '../panels/utils/panelsContainer';
import { resetPanelCenterTree } from '../panels/utils/panelsCenterStore';
import { isGFT, isULA } from '__common/constants/products';
import { getPanelArrayStageBounds, getPanelArrayStageCenter } from '../panels/utils/calculatePanelsRange';
import { markStageDirty } from './stageDirty';

const classicDesignerZoom = {
  height: 99.68746373276178,
  width: 169.5780322696522,
  x: 637.2108721432896 - 50,
  y: 302.6578361877598 - 50,
};

export function zoomRoof(
  stage: PIXI.Container,
  renderer: PIXI.Renderer,
  editorWidth: number,
  editorHeight: number,
  classicDesigner?: boolean,
  productId?: number,
  screenshots = false,
) {
  // Fit the rendered ULA array rather than its nominal roof bounds. Blank-map
  // tables can extend past those bounds, and large arrays need to zoom out.
  if (screenshots && classicDesigner && isULA(productId)) {
    const container = getPanelsContainer();
    if (!container || !container.children.length) return false;
    const bounds = container.getBounds();
    if (bounds.width <= 0 || bounds.height <= 0) return false;
    const padding = 40;
    const factor = Math.min(
      (editorWidth - padding * 2) / bounds.width,
      (editorHeight - padding * 2) / bounds.height,
    );
    if (!Number.isFinite(factor) || factor <= 0) return false;
    const centerX = bounds.x + bounds.width / 2;
    const centerY = bounds.y + bounds.height / 2;
    stage.x = editorWidth / 2 - (centerX - stage.x) * factor;
    stage.y = editorHeight / 2 - (centerY - stage.y) * factor;
    stage.scale.set(stage.scale.x * factor, stage.scale.y * factor);
    markStageDirty();
    renderer.render(stage);
    return true;
  }

  const {
    background: { bgXY, selectedRoofId },
    drawingManager: { roofs },
    projectConfiguration : { projectEnvConfig : { is_aurora_project, is_os_project } },
  } = state();
  const savedPanels = roofs[selectedRoofId].panels
    ? roofs[selectedRoofId].panels
    : [];
  const livePanels = state().panels.panels;
  const panels = classicDesigner && livePanels.length > 0 ? livePanels : savedPanels;
  const previewMapWidth = 0;
  const roofScale = 1;
  let roofEdgesBounds;
  let mouse_loc;
  if (classicDesigner && panels.length) {
    // initCanvas() starts animate() before loadPanels(). Centering from
    // drawingManager.saved panels in that window shifts obstruction coords
    // via CENTER_OBSTRUCTIONS_ON_ROOF, then LOAD_PANELS consumes
    // centerOffset against an empty live array. Each later editor visit
    // repeats that offset, so blank-map obstructions walk from their place.
    if (!livePanels.length) {
      return false;
    }

    const { settings: { canvasCenter } } = state();
    roofEdgesBounds = getPanelArrayStageBounds(panels, canvasCenter);
    if (!roofEdgesBounds) {
      return false;
    }

    mouse_loc = bgXY;

    // Hack for making it look like the array is in the center, in order to
    // make sure the array is not breaking when we go in/out of the editor.
    centerAllPanels({
      x: roofEdgesBounds.x + roofEdgesBounds.width / 2.1,
      y: roofEdgesBounds.y + roofEdgesBounds.height / 2.1,
    }, mouse_loc);

    // const dot: any = new PIXI.Graphics();
    // dot.beginFill(0x00ff00, 1);
    // dot.drawCircle(mouse_loc.x, mouse_loc.y, 5);
    // dot.id = 'endLineRoofEdgeDot';
    // dot.zIndex = 100001;
    // dot.endFill();

    // const rect: any = new PIXI.Graphics();
    // rect.lineStyle(1, 0x000000);
    // rect.drawRect(
    //   roofEdgesBounds.x,
    //   roofEdgesBounds.y,
    //   roofEdgesBounds.width,
    //   roofEdgesBounds.height,
    // );
    // rect.id = 'rect';
    // rect.zIndex = 500;
    // rect.endFill();

    // stage.addChild(dot);
    // stage.addChild(rect);
  } else if (classicDesigner && panels.length === 0) {
    roofEdgesBounds = classicDesignerZoom;
    mouse_loc = bgXY;
  } else {
    const roofEdges = getRoofEdges();
    roofEdgesBounds = roofEdges.getBounds();
    mouse_loc = bgXY;
  }

  if (roofEdgesBounds.x < 0 && roofEdgesBounds.y < 0) {
    return false;
  }

  if (panels.length) {
    if (is_aurora_project || is_os_project) {
      roofEdgesBounds.width /= 0.2;
      roofEdgesBounds.height /= 0.2;
    } else {
      roofEdgesBounds.width /= 0.5;
      roofEdgesBounds.height /= 0.5;
    }
  }

  let roofDimensionForScale;
  let editorDimensionForScale;
  let scale;
  const multiplicationfactor = isGFT(productId) ? 0.25 : 1;

  if (roofEdgesBounds.width > roofEdgesBounds.height) {
    roofDimensionForScale = roofEdgesBounds.width * roofScale;
    editorDimensionForScale = editorWidth - previewMapWidth;
    scale = multiplicationfactor * editorDimensionForScale / roofDimensionForScale;
  } else {
    roofDimensionForScale = roofEdgesBounds.height * roofScale;
    editorDimensionForScale = editorHeight;
    scale = multiplicationfactor * editorDimensionForScale / roofDimensionForScale;
  }
  if (scale < 1) {
    return true;
  }

  const zoom_factor = scale;

  stage.x -= (mouse_loc.x - stage.x) * (zoom_factor - 1);
  stage.y -= (mouse_loc.y - stage.y) * (zoom_factor - 1);
  stage.scale.set(scale);
  markStageDirty();
  renderer.render(stage);
  return true;
}

// Store wheel handler reference for proper cleanup
let storedWheelHandler: ((e: WheelEvent) => void) | null = null;

export const initZoom = (domElement, stage: PIXI.Container, renderer) => {
  // PIXI v7: Use modern 'wheel' event for better browser compatibility
  // Store the handler reference for proper cleanup
  storedWheelHandler = (e) => {
    onWheelScrool(e, stage, renderer);
  };
  
  // Add both 'wheel' (modern) and 'mousewheel' (legacy) for maximum compatibility
  domElement.addEventListener('wheel', storedWheelHandler);
  domElement.addEventListener('mousewheel', storedWheelHandler); // Fallback for older browsers
};

export const terminateZoom = (
  domElement,
  stage: PIXI.Container,
  renderer: PIXI.Renderer,
) => {
  // PIXI v7: Remove both event listeners properly using stored reference
  if (storedWheelHandler) {
    domElement.removeEventListener('wheel', storedWheelHandler);
    domElement.removeEventListener('mousewheel', storedWheelHandler);
    storedWheelHandler = null;
  }
};

export const zoom = (
  e,
  stage: PIXI.Container,
  renderer: PIXI.Renderer,
  toPoint?: { x: number; y: number },
) => {
  const zoom_in = e.deltaY < 0;
  let zoom_factor;
  if (zoom_in) {
    zoom_factor = 1.1;
  } else {
    zoom_factor = 1 / 1.1;
  }

  // zoom
  stage.scale.x *= zoom_factor;
  stage.scale.y *= zoom_factor;

  // center on cursor
  const mouse_loc = toPoint || { x: 0, y: 0 }; // Fallback for PIXI.js v7 interaction changes
  stage.x -= (mouse_loc.x - stage.x) * (zoom_factor - 1);
  stage.y -= (mouse_loc.y - stage.y) * (zoom_factor - 1);

  // keep in frame
  stage.x = Math.min(0, stage.x);
  stage.y = Math.min(0, stage.y);

  // PIXI v7: Properly access canvas dimensions
  const canvas = renderer.view as HTMLCanvasElement;
  const renderer_view_width = canvas.width - 100;
  const renderer_view_height = canvas.height - 100;


  // keep width in bounds
  const visible_width = renderer.width * stage.scale.x + stage.x;
  if (visible_width < renderer_view_width) {
    stage.x = Math.min(0, renderer_view_width - renderer.width * stage.scale.x);
    if (stage.x === 0) {
      stage.scale.x = renderer_view_width / renderer.width;
    }
  }

  // keep height in bounds
  const visible_height = renderer.height * stage.scale.y + stage.y;
  if (visible_height < renderer_view_height) {
    stage.y = Math.min(
      0,
      renderer_view_height - renderer.height * stage.scale.y,
    );
    if (stage.y === 0) {
      stage.scale.y = renderer_view_height / renderer.height;
    }
  }

  // keep aspect ratio
  if (stage.scale.y !== stage.scale.x) {
    stage.scale.x = Math.max(stage.scale.x, stage.scale.y);
    stage.scale.y = Math.max(stage.scale.x, stage.scale.y);
  }

  dispatch(SET_STAGE_SCALE({ x: stage.scale.x, y: stage.scale.y }));
  markStageDirty();
  if (e.preventDefault) {
    e.preventDefault();
  }
};

function onWheelScrool(
  e,
  stage: PIXI.Container,
  renderer: PIXI.Renderer,
) {
  // PIXI v7: Better wheel event handling for different browsers
  e.preventDefault(); // Prevent page scroll
  
  // Normalize wheel delta across browsers
  let deltaY = e.deltaY;
  if (e.wheelDelta !== undefined) {
    // Legacy mousewheel event
    deltaY = -e.wheelDelta;
  }

  // PIXI v7: Calculate mouse position relative to the canvas
  // In PIXI v7, renderer.view is HTMLCanvasElement
  const canvas = renderer.view as HTMLCanvasElement;
  const rect = canvas.getBoundingClientRect();
  const mousePoint = {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top,
  };

  zoom(e, stage, renderer, mousePoint);
}


export function centerPanelsForBlankMap() {
  const {
    background: { bgXY },
    panels: { panels: livePanels },
    settings: { canvasCenter },
  } = state();
  if (!livePanels.length) return;

  const panelsContainerCenter = getPanelArrayStageCenter(livePanels, canvasCenter);
  if (!panelsContainerCenter) return;

  centerAllPanels(panelsContainerCenter, bgXY);
}

function centerAllPanels(panelsContainerCenter, canvasCenter) {
  const { background: { selectedRoofId, metersPerPixel }, settings: { rowSpacing, columnSpacing } } = state();
  const panelsContainer = getPanelsContainer();
  const { panels, exposureRecaluclationHash } = state().panels;
  if (!panels.length) {
    return;
  }
  const offsetX = canvasCenter.x - panelsContainerCenter.x;
  const offsetY = canvasCenter.y - panelsContainerCenter.y;
  if (Math.abs(offsetX) < 0.5 && Math.abs(offsetY) < 0.5) {
    return;
  }

  dispatch(SET_CENTER_OFFSET({ x: offsetX, y: offsetY }));
  dispatch(CENTER_OBSTRUCTIONS_ON_ROOF(offsetX, offsetY, selectedRoofId));
  resetKdTree();
  resetPanelCenterTree();
  clearRTree();
  removeAllPanelsFromStageToRedraw(panelsContainer, getBaysContainer());
  dispatch(LOAD_PANELS(panels, true, rowSpacing, columnSpacing, metersPerPixel, exposureRecaluclationHash));
}
